import type { LeadDetail } from '@borradh-workspace/api-client/types';
import { leadFormFieldTypeLabels } from '@borradh-workspace/labels';

export interface FormAnswer {
  key: string;
  label: string | null;
  values: string[];
  isContact: boolean;
  status: 'answered' | 'unanswered' | 'missing';
}

export interface FormResponse {
  formName: string | null;
  submittedAt: string | null;
  adId: string | null;
  campaignId: string | null;
  answers: FormAnswer[];
}

const METADATA_KEYS = new Set([
  'form_id',
  'page_id',
  'ad_id',
  'campaign_id',
  'is_organic',
  'created_time',
  'microsite_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
]);
const CONTACT_KEYS = new Set([
  'full_name',
  'fullname',
  'first_name',
  'firstname',
  'last_name',
  'lastname',
  'email',
  'phone_number',
  'phone',
]);
const FIELD_NAMES: Record<string, string> = {
  PHONE: 'phone_number',
  ZIP: 'zip_code',
  DATE_OF_BIRTH: 'date_of_birth',
};

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

function valuesOf(value: unknown): string[] {
  const values = Array.isArray(value) ? value : [value];
  return values.flatMap((v) =>
    typeof v === 'string'
      ? [v]
      : typeof v === 'number' || typeof v === 'boolean'
        ? [String(v)]
        : []
  );
}

/** Display the existing records; nothing is copied or persisted. */
export function getLeadFormResponse(lead: LeadDetail): FormResponse | null {
  if (lead.source !== 'meta_lead_form' && lead.source !== 'website')
    return null;
  const data =
    lead.formData &&
    typeof lead.formData === 'object' &&
    !Array.isArray(lead.formData)
      ? (lead.formData as Record<string, unknown>)
      : {};
  const fields = new Map(
    Object.entries(data)
      .filter(([key]) => !METADATA_KEYS.has(key.toLowerCase()))
      .map(([key, value]) => [
        key.toLowerCase(),
        { key, values: valuesOf(value) },
      ])
  );
  // A website enquiry has no Meta form definition: its keys already contain
  // the questions exactly as shown in the funnel. Routing fields are not answers.
  const website = lead.source === 'website';
  if (website && fields.size === 0) return null;
  const name = [
    lead.firstName === 'Unknown' ? null : lead.firstName,
    lead.lastName,
  ]
    .filter(Boolean)
    .join(' ');
  const contacts: Record<string, string | null> = {
    full_name: text(name),
    fullname: text(name),
    first_name: lead.firstName === 'Unknown' ? null : text(lead.firstName),
    firstname: lead.firstName === 'Unknown' ? null : text(lead.firstName),
    last_name: text(lead.lastName),
    lastname: text(lead.lastName),
    email: text(lead.email),
    phone_number: text(lead.phone),
    phone: text(lead.phone),
  };
  const answers: FormAnswer[] = [];
  const consumed = new Set<string>();
  for (const question of (website ? [] : lead.sourceLeadForm?.questions) ??
    []) {
    const key =
      question.key ||
      (question.type === 'CUSTOM'
        ? null
        : (FIELD_NAMES[question.type] ?? question.type.toLowerCase()));
    if (!key) continue;
    const lookup = key.toLowerCase();
    if (consumed.has(lookup)) continue;
    consumed.add(lookup);
    const isContact = CONTACT_KEYS.has(lookup);
    const stored = fields.get(lookup);
    const contact = contacts[lookup];
    const values = stored?.values ?? (isContact && contact ? [contact] : []);
    const options = new Map(
      (question.options ?? []).map((o) => [o.key, o.value])
    );
    answers.push({
      key,
      label:
        text(question.label) ??
        (question.type === 'CUSTOM'
          ? null
          : leadFormFieldTypeLabels[question.type]),
      values: values.map((v) => options.get(v) ?? v),
      isContact,
      status: values.some((v) => v.trim())
        ? 'answered'
        : stored
          ? 'unanswered'
          : 'missing',
    });
  }
  // Keep every stored answer even when its question no longer exists or the
  // form definition was never synced. Identifiers are never shown as answers.
  for (const [lookup, field] of fields) {
    if (consumed.has(lookup)) continue;
    answers.push({
      ...field,
      label: website ? field.key : null,
      isContact: CONTACT_KEYS.has(lookup),
      status: field.values.some((v) => v.trim()) ? 'answered' : 'unanswered',
    });
  }
  // Without a definition we cannot know which contact questions were asked.
  // The profile already shows the current contact details.
  const funnel = (lead.metadata as { funnel?: { startedAt?: unknown } } | null)
    ?.funnel;
  const createdTime =
    text(data.created_time) ?? (website ? text(funnel?.startedAt) : null);
  return {
    formName: website
      ? `${text(data.Treatment) ?? 'Website'} booking enquiry`
      : (lead.sourceLeadForm?.name ?? null),
    submittedAt:
      createdTime && !Number.isNaN(new Date(createdTime).getTime())
        ? createdTime
        : website
          ? lead.createdAt
          : null,
    adId: text(data.ad_id),
    campaignId: text(data.campaign_id),
    answers,
  };
}
